import { createContext, useCallback, useContext, useState, type ComponentType, type ReactNode } from 'react'
import { MODAL_COMPONENTS, type ModalId, type ModalRegistry } from 'components/organisms/modals/modal-registry'

interface ModalSlot {
	id: ModalId
	instanceKey: string
	props: unknown
}

interface ModalManagerContextValue {
	/**
	 * Open a registered modal. Returns an instanceKey that can be passed to
	 * closeModal() to target this specific instance (useful when stacking).
	 */
	openModal: <K extends ModalId>(id: K, props: ModalRegistry[K]) => string
	/** Close a specific modal instance by the key returned from openModal(). */
	closeModal: (instanceKey: string) => void
	/** Close every open modal (e.g. on route change). */
	closeAllModals: () => void
	/** True when at least one instance of the given id is open. */
	isModalOpen: (id: ModalId) => boolean
}

const ModalManagerContext = createContext<ModalManagerContextValue | undefined>(undefined)

let counter = 0

export function ModalManagerProvider({ children }: { children: ReactNode }) {
	const [stack, setStack] = useState<ModalSlot[]>([])

	const openModal = useCallback(<K extends ModalId>(id: K, props: ModalRegistry[K]): string => {
		const instanceKey = `modal-${++counter}`
		setStack(prev => [...prev, { id, instanceKey, props }])
		return instanceKey
	}, [])

	const closeModal = useCallback((instanceKey: string) => {
		setStack(prev => prev.filter(s => s.instanceKey !== instanceKey))
	}, [])

	const closeAllModals = useCallback(() => setStack([]), [])

	const isModalOpen = useCallback(
		(id: ModalId) => stack.some(s => s.id === id),
		[stack],
	)

	return (
		<ModalManagerContext.Provider value={{ openModal, closeModal, closeAllModals, isModalOpen }}>
			{children}
			{stack.map(slot => {
				// ComponentType<any> required: slot.props is stored as `unknown` to support
				// heterogeneous modal types in the stack. Type correctness is enforced at
				// the openModal<K>() call site via the ModalRegistry mapped type.
				const Component = MODAL_COMPONENTS[slot.id] as ComponentType<any>
				return (
					<Component
						key={slot.instanceKey}
						{...(slot.props as object)}
						onClose={() => closeModal(slot.instanceKey)}
					/>
				)
			})}
		</ModalManagerContext.Provider>
	)
}

export function useModal() {
	const ctx = useContext(ModalManagerContext)
	if (!ctx) throw new Error('useModal must be used within ModalManagerProvider')
	return ctx
}
